import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, MapPin, Users, CalendarRange, Construction } from "lucide-react";
import api, { cachedGet } from "@/lib/api";
import { euro } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATI, STATO_STYLE, fmtDay } from "@/lib/cantieri";
import CantiereDialog from "@/components/cantieri/CantiereDialog";
import CantiereDetail from "@/components/cantieri/CantiereDetail";
import { OperaiTab } from "@/components/cantieri/OperaiTab";

const Kpi = ({ label, value, testid }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
    <div className="font-heading text-2xl font-extrabold mt-1" data-testid={testid}>{value}</div>
  </div>
);

const SiteCard = ({ c, onOpen }) => (
  <button type="button" onClick={() => onOpen(c)} data-testid={`cantiere-card-${c.id}`}
    className="text-left rounded-xl border border-border bg-card p-4 space-y-3 hover:border-orange-300 hover:shadow-md transition-[box-shadow,border-color] border-l-4 border-l-orange-500">
    <div className="flex items-start gap-2">
      <div className="flex-1 min-w-0"><div className="font-heading font-bold truncate">{c.nome}</div><div className="text-sm text-muted-foreground truncate">{c.cliente_nome || "—"}</div></div>
      <Badge variant="outline" className={STATO_STYLE[c.stato]}>{STATI[c.stato]}</Badge>
    </div>
    <div className="text-xs text-muted-foreground space-y-1">
      <div className="flex items-center gap-1.5"><CalendarRange className="w-3.5 h-3.5" /> {fmtDay(c.data_inizio)} → {fmtDay(c.data_fine)} · {c.giorni_lavorativi} gg</div>
      {c.indirizzo && <div className="flex items-center gap-1.5 truncate"><MapPin className="w-3.5 h-3.5 shrink-0" /> {c.indirizzo}</div>}
      <div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {c.calcolo === "libero" ? `${c.operai_presenti || 0} ${c.operai_presenti === 1 ? "operaio" : "operai"} · ${c.ore_lavorate} ore lavorate${c.ore_previste ? ` su ${c.ore_previste}` : ""}` : `${c.num_operai} ${c.num_operai === 1 ? "operaio" : "operai"} · ${c.ore_lavorate}/${c.ore_previste} ore`}</div>
    </div>
    <Progress value={c.avanzamento} className="h-1.5" />
    <div className="flex justify-between text-xs"><span>Incassato <b>{euro(c.totale_incassato)}</b></span>{c.residuo != null && <span>Residuo <b>{euro(c.residuo)}</b></span>}</div>
    {c.guadagno != null && (
      <div className="flex justify-between text-xs" data-testid={`cantiere-card-profit-${c.id}`}>
        <span>Spese <b>{euro(c.totale_spese)}</b></span>
        <span>Guadagno <b className={c.guadagno < 0 ? "text-destructive" : "text-emerald-600 dark:text-emerald-400"}>{euro(c.guadagno)}</b></span>
      </div>
    )}
  </button>
);

export default function Cantieri() {
  const [cantieri, setCantieri] = useState([]);
  const [operai, setOperai] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [transfer, setTransfer] = useState(false);
  const [edit, setEdit] = useState(null);
  const [view, setView] = useState(null);
  const [q, setQ] = useState("");
  const [stato, setStato] = useState("attivi");
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "operai" ? "operai" : "cantieri");

  const load = useCallback(() => {
    api.get("/cantieri").then((r) => setCantieri(r.data));
    api.get("/operai").then((r) => setOperai(r.data));
  }, []);
  useEffect(() => {
    load();
    Promise.all([cachedGet("/contacts"), cachedGet("/quotes"), cachedGet("/invoices")]).then(([c, qs, inv]) => { setContacts(c.data); setQuotes(qs.data); setInvoices(inv.data); }).catch(() => {});
  }, [load]);
  useEffect(() => {
    if (params.get("view")) setView(params.get("view"));
    if (params.get("new")) setEdit({ initial: null });
    if (params.get("tab") === "operai") setTab("operai");
    if (params.get("spese")) setTransfer(true);
    if (params.get("view") || params.get("new") || params.get("tab")) setParams({}, { replace: true });
  }, [params, setParams]);

  const filtered = useMemo(() => cantieri.filter((c) => (stato === "tutti" || (stato === "attivi" ? c.stato !== "completato" : c.stato === stato))
    && (!q || `${c.nome} ${c.cliente_nome} ${c.indirizzo}`.toLowerCase().includes(q.toLowerCase()))), [cantieri, q, stato]);
  const attivi = cantieri.filter((c) => c.stato === "in_corso" || c.stato === "programmato");
  const sum = (k) => cantieri.reduce((s, c) => s + (Number(c[k]) || 0), 0);

  return (
    <div className="space-y-5 animate-in-up" data-testid="cantieri-page">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Cantieri aperti" value={attivi.length} testid="kpi-sites-open" />
        <Kpi label="Ore lavorate" value={sum("ore_lavorate").toLocaleString("it-IT")} testid="kpi-sites-hours" />
        <Kpi label="Incassato" value={euro(sum("totale_incassato"))} testid="kpi-sites-advances" />
        <Kpi label="Residuo da incassare" value={euro(cantieri.reduce((s, c) => s + Math.max(0, c.residuo || 0), 0))} testid="kpi-sites-residual" />
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <TabsList><TabsTrigger value="cantieri" data-testid="tab-cantieri">Cantieri</TabsTrigger><TabsTrigger value="operai" data-testid="tab-operai">Operai ({operai.length})</TabsTrigger></TabsList>
        </div>
        <TabsContent value="cantieri" className="space-y-4 pt-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cantiere, cliente, indirizzo…" className="pl-9" data-testid="cantieri-search-input" /></div>
            <Select value={stato} onValueChange={setStato}>
              <SelectTrigger className="w-40" data-testid="cantieri-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="attivi">Non completati</SelectItem><SelectItem value="tutti">Tutti</SelectItem>{Object.entries(STATI).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Button className="ml-auto" onClick={() => setEdit({ initial: null })} data-testid="new-cantiere-button"><Plus className="w-4 h-4 mr-1.5" /> Nuovo cantiere</Button>
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">{filtered.map((c) => <SiteCard key={c.id} c={c} onOpen={(x) => setView(x.id)} />)}</div>
          {filtered.length === 0 && <div className="rounded-xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground"><Construction className="w-8 h-8 mx-auto mb-2 opacity-40" />Nessun cantiere. Creane uno: va in agenda in automatico.</div>}
        </TabsContent>
        <TabsContent value="operai" className="pt-3"><OperaiTab operai={operai} onChange={load} openTransfer={transfer} /></TabsContent>
      </Tabs>
      <CantiereDialog open={!!edit} initial={edit?.initial} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} operai={operai} contacts={contacts} quotes={quotes} invoices={invoices} />
      <CantiereDetail id={view} operai={operai} onClose={() => setView(null)} onEdit={(c) => { setView(null); setEdit({ initial: c }); }} onChanged={load} />
    </div>
  );
}
